import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import CtaClose from '../common/CtaClose'
import trackEvent from '../../utils/trackEvent'
import { getMaestroContent } from '../../content/maestroContent'

/*
 * Maestro, the flagship, on its own stage.
 *
 * THESIS: the page argues WHY delivery needs a new operating system before it
 *   shows a single screen; every product frame appears only as the answer to a
 *   management question. Refuses the feature-grid SaaS homepage outright.
 * OWN-WORLD: the board's own state semantics ARE the palette. Lime is done,
 *   orange is in motion or at risk, cyan is queued: the tri-accent used as
 *   product truth, not decoration. Graphite field shared with the site.
 * STORY: an executive recognizes that AI broke their management assumptions,
 *   meets the six questions a leader should always be able to answer, sees the
 *   operating model behind the software, and only then meets the screens.
 * FIRST VIEWPORT: the thesis headline over a live systems diagram: workers
 *   (human and agent) flowing through Maestro's policy gate into a burn-up
 *   that renders the truth. Tokens animate along the paths; reduced motion
 *   freezes everything.
 * FORM: HUD-diagram language at the craft level of the FDE deck, committed to
 *   the site's graphite world; pinned brief (founder positioning doc, Sep 2026).
 */

const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null)
  const [state, setState] = useState('shown')
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    if (el.getBoundingClientRect().top <= window.innerHeight * 0.86) return undefined
    setState('hidden')
    const io = new IntersectionObserver(
      (e) => { if (e[0] && e[0].isIntersecting) { setState('in'); io.disconnect() } },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' }
    )
    io.observe(el)
    const t = setTimeout(() => { setState('in'); io.disconnect() }, 2400)
    return () => { io.disconnect(); clearTimeout(t) }
  }, [])
  return <div ref={ref} className={`mrv ${state === 'hidden' ? 'mrv-off' : 'mrv-on'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>
}

const Kicker = ({ children, tone = 'text-pyramid-19' }) => (
  <p className={`text-[11px] font-semibold uppercase tracking-[0.32em] ${tone}`}>{children}</p>
)

const scrollToId = (id) => { const el = typeof document !== 'undefined' && document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }) }

// ── shared SVG defs: glow filter + line gradient in the board's colors ──────
const Defs = ({ id = 'm' }) => (
  <defs>
    <filter id={`${id}g`} x="-40%" y="-40%" width="180%" height="180%">
      <feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    <linearGradient id={`${id}line`} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor="#155e73" /><stop offset="0.55" stopColor="#19a2cf" /><stop offset="1" stopColor="#c9d33a" />
    </linearGradient>
    <radialGradient id={`${id}node`} cx="0.5" cy="0.42" r="0.6">
      <stop offset="0" stopColor="#1a212a" /><stop offset="1" stopColor="#0d1218" />
    </radialGradient>
  </defs>
)

// ── hero: workers -> policy gate -> the truth, rendered ─────────────────────
const HeroDiagram = ({ s }) => (
  <svg viewBox="0 0 1000 340" className="w-full" role="img" aria-label={s.aria}>
    <Defs id="mh" />
    <defs>
      {/* the product's done-area gradient: lime fading to nothing at the baseline */}
      <linearGradient id="mharea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#c9d33a" stopOpacity="0.30" />
        <stop offset="0.6" stopColor="#c9d33a" stopOpacity="0.10" />
        <stop offset="1" stopColor="#c9d33a" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* worker -> Maestro connectors */}
    {[76, 142, 208, 274].map((y, i) => (
      <path key={y} id={`mw${i}`} className="mflow" strokeWidth="1.5" d={`M226 ${y} C 320 ${y}, 330 ${175 - (1.5 - i) * 8}, 408 ${168 - (1.5 - i) * 4}`} style={{ animationDelay: `${i * 0.25}s` }} />
    ))}
    {/* Maestro -> chart connector */}
    <path id="mout" className="mflow" strokeWidth="2" d="M608 168 C 660 168, 668 168, 706 168" />
    {/* work tokens */}
    {[0, 1, 2, 3].map((i) => (
      <circle key={`t${i}`} className="mtok" r="2.7"><animateMotion dur={`${2.5 + (i % 3) * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.4}s`}><mpath xlinkHref={`#mw${i}`} /></animateMotion></circle>
    ))}
    {[0, 1].map((i) => (
      <circle key={`o${i}`} className="mtok mtok-done" r="3"><animateMotion dur="1.8s" repeatCount="indefinite" begin={`${0.9 + i * 0.9}s`}><mpath xlinkHref="#mout" /></animateMotion></circle>
    ))}
    {/* the workers */}
    <text x="130" y="40" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="700" letterSpacing="1.6">{s.workersLabel}</text>
    {s.workers.map((w, i) => {
      const agent = i > 1
      return (
        <g key={`${w}-${i}`}>
          <rect x="34" y={54 + i * 66} width="192" height="44" rx="10" fill="url(#mhnode)" stroke={agent ? 'rgba(248,129,22,.35)' : 'rgba(255,255,255,.14)'} />
          <circle cx="58" cy={76 + i * 66} r="4" fill={agent ? '#f88116' : '#19a2cf'} />
          <text x="74" y={81 + i * 66} fill="#cbd5e1" fontSize="12" fontWeight="600">{w}</text>
        </g>
      )
    })}
    {/* the operating system, with its written policies */}
    <g>
      <rect x="408" y="46" width="200" height="248" rx="16" fill="#11161c" stroke="#19a2cf" strokeWidth="1.5" filter="url(#mhg)" opacity="0.97" />
      <text x="508" y="84" textAnchor="middle" fill="#ffffff" fontSize="21" fontWeight="700" fontFamily="Quicksand, sans-serif">{s.center}</text>
      <text x="508" y="103" textAnchor="middle" fill="#4cbfe0" fontSize="9.5" fontWeight="700" letterSpacing="1.4">{s.centerSub}</text>
      {s.gates.map((g, i) => (
        <g key={g}>
          <rect className="mgate" style={{ animationDelay: `${-i * 1.9}s` }} x="428" y={126 + i * 52} width="160" height="40" rx="9" fill="rgba(25,162,207,.07)" stroke="rgba(25,162,207,.3)" />
          <circle className="mgate-dot" style={{ animationDelay: `${-i * 1.9}s` }} cx="448" cy={146 + i * 52} r="4.5" fill="#19a2cf" />
          <text x="462" y={150 + i * 52} fill="#dbe8fb" fontSize="10.5" fontWeight="600">{g}</text>
        </g>
      ))}
    </g>
    {/* the truth, rendered: the product's hero burn-up, in miniature.
        Same grammar as maestro-shell charts.js: lime executed line over a
        gradient area, dashed muted scope step running on to the commitment
        dot, dotted planned line, orange deadline vertical, and the forecast
        fan (best lime, likely cyan, worst orange) opening from the tip. */}
    <g>
      <text x="836" y="40" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="700" letterSpacing="1.6">{s.rightLabel}</text>
      <rect x="706" y="54" width="260" height="240" rx="14" fill="url(#mhnode)" stroke="rgba(255,255,255,.14)" />
      {/* the computed judgment */}
      <rect x="748" y="66" width="86" height="24" rx="12" fill="rgba(201,211,58,.12)" stroke="rgba(201,211,58,.45)" />
      <text x="791" y="82" textAnchor="middle" fill="#c9d33a" fontSize="10.5" fontWeight="700" letterSpacing="1">{s.chip}</text>
      <text x="748" y="103" textAnchor="start" fill="#64748b" fontSize="9">{s.chipSub}</text>
      {/* baseline */}
      <path d="M728 260 L950 260" stroke="rgba(255,255,255,.16)" strokeWidth="1" />
      {/* commitment: target vertical, and the dot where scope lands on it */}
      <path d="M940 114 L940 260" stroke="rgba(148,163,184,.5)" strokeWidth="1.1" strokeDasharray="6 3" />
      {/* deadline milepost */}
      <path d="M884 132 L884 260" stroke="rgba(248,129,22,.65)" strokeWidth="1" strokeDasharray="3 3" />
      {/* planned: zero to scope at the commitment */}
      <path d="M728 260 L940 143" stroke="rgba(148,163,184,.55)" strokeWidth="1.2" strokeDasharray="2 4" fill="none" />
      {/* scope: the dashed step, running on to the commitment */}
      <path d="M728 172 L756 172 L756 162 L788 162 L788 150 L826 150 L826 143 L940 143" stroke="rgba(148,163,184,.75)" strokeWidth="1.3" strokeDasharray="5 4" fill="none" />
      <circle cx="940" cy="143" r="3.4" fill="#0d1218" stroke="#94a3b8" strokeWidth="1.4" />
      {/* executed: gradient area, lime line, end dot */}
      <path d="M728 256 C 752 250, 768 236, 786 224 C 806 210, 830 200, 848 194 L848 260 L728 260 Z" fill="url(#mharea)" />
      <path className="mburn" d="M728 256 C 752 250, 768 236, 786 224 C 806 210, 830 200, 848 194" stroke="#c9d33a" strokeWidth="2.4" fill="none" filter="url(#mhg)" />
      <circle className="mpulse-dot" cx="848" cy="194" r="3.6" fill="#c9d33a" stroke="#0d1218" strokeWidth="1.5" />
      <text className="mlabel" x="842" y="186" textAnchor="end" fill="#c9d33a" fontSize="10" fontWeight="700">{s.doneLabel}</text>
      {/* the forecast fan, opening from the executed tip */}
      <g style={{ transformOrigin: '848px 194px', transformBox: 'view-box' }} className="mfan">
        <path d="M848 194 L922 147" stroke="#c9d33a" strokeWidth="1.4" strokeDasharray="6 4" fill="none" />
        <path d="M848 194 L940 164" stroke="#4cbfe0" strokeWidth="1.4" strokeDasharray="6 4" fill="none" />
        <path d="M848 194 L952 186" stroke="#f88116" strokeWidth="1.4" strokeDasharray="6 4" fill="none" />
      </g>
      <text className="mlabel" x="838" y="281" textAnchor="middle" fontSize="9.5" fontWeight="600">
        <tspan fill="#c9d33a">{s.fanBest}</tspan>
        <tspan fill="#475569">{' · '}</tspan>
        <tspan fill="#4cbfe0">{s.fanLikely}</tspan>
        <tspan fill="#475569">{' · '}</tspan>
        <tspan fill="#f88116">{s.fanWorst}</tspan>
      </text>
    </g>
  </svg>
)

// ── the operating model, top to bottom, with the feedback loop ──────────────
const ModelDiagram = ({ s }) => (
  <svg viewBox="0 0 520 560" className="w-full max-w-[440px]" role="img" aria-label={s.aria}>
    <Defs id="mm" />
    {/* descent spine */}
    <path className="mflow" strokeWidth="1.6" d="M260 66 L260 96" />
    <path className="mflow" strokeWidth="1.6" d="M260 190 L260 216" />
    <path className="mflow" strokeWidth="1.6" d="M170 300 L120 336 M260 300 L260 336 M350 300 L400 336" />
    <path className="mflow" strokeWidth="1.6" d="M120 392 L232 428 M260 392 L260 428 M400 392 L288 428" />
    <path className="mflow" strokeWidth="1.6" d="M260 470 L260 492" />
    {/* feedback return, climbing back up the side */}
    <path className="mfeed" d="M368 508 C 490 500, 496 300, 496 260 C 496 150, 420 128, 372 126" />
    <circle className="mtok" r="2.6"><animateMotion dur="3.6s" repeatCount="indefinite"><mpath xlinkHref="#mmfb" /></animateMotion></circle>
    <path id="mmfb" d="M368 508 C 490 500, 496 300, 496 260 C 496 150, 420 128, 372 126" fill="none" stroke="none" />
    <text x="500" y="318" fill="#64748b" fontSize="9" transform="rotate(90 500 318)" textAnchor="middle" letterSpacing="1">{s.feedback}</text>
    {/* strategy */}
    <text x="260" y="52" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="700" letterSpacing="1.8">{s.strategy}</text>
    {/* the pyramid, descending: cyan apex to lime base like the canon */}
    <g>
      {[
        ['w1', 100, 220, '#19a2cf'],
        ['w2', 130, 284, '#f88116'],
        ['w3', 160, 348, '#c9d33a'],
      ].map(([k, y, w, color]) => (
        <path key={k} d={`M${260 - w / 2 + 8} ${y} L${260 + w / 2 - 8} ${y} L${260 + w / 2} ${y + 26} L${260 - w / 2} ${y + 26} Z`} fill={color} opacity="0.75" />
      ))}
    </g>
    <text x="140" y="106" fill="#e2e8f0" fontSize="12" fontWeight="700" fontFamily="Quicksand, sans-serif" textAnchor="end">{s.pyramid}</text>
    <text x="140" y="122" fill="#64748b" fontSize="10" textAnchor="end">{s.pyramidSub}</text>
    {/* Maestro */}
    <g>
      <rect x="150" y="216" width="220" height="84" rx="14" fill="#11161c" stroke="#19a2cf" strokeWidth="1.5" filter="url(#mmg)" />
      <text x="260" y="252" textAnchor="middle" fill="#ffffff" fontSize="20" fontWeight="700" fontFamily="Quicksand, sans-serif">{s.maestro}</text>
      <text x="260" y="274" textAnchor="middle" fill="#4cbfe0" fontSize="10" fontWeight="700" letterSpacing="1.6">{s.maestroSub.toUpperCase()}</text>
    </g>
    {/* the workers */}
    {s.workers.map((w, i) => {
      const x = [120, 260, 400][i]
      const agent = i > 0
      return (
        <g key={`${w}-${i}`}>
          <rect x={x - 62} y="336" width="124" height="56" rx="11" fill="url(#mmnode)" stroke={agent ? 'rgba(248,129,22,.4)' : 'rgba(255,255,255,.16)'} />
          <circle cx={x - 42} cy="364" r="4" fill={agent ? '#f88116' : '#19a2cf'} />
          <text x={x - 28} y="368" fill="#cbd5e1" fontSize="11.5" fontWeight="600">{w}</text>
        </g>
      )
    })}
    {/* flow and what it teaches */}
    <rect x="152" y="428" width="216" height="42" rx="10" fill="url(#mmnode)" stroke="rgba(201,211,58,.35)" />
    <text x="260" y="454" textAnchor="middle" fill="#c9d33a" fontSize="11" fontWeight="700" letterSpacing="1.6">{s.flow}</text>
    <text x="260" y="512" textAnchor="middle" fill="#94a3b8" fontSize="10.5" fontWeight="700" letterSpacing="1.4">{s.observed}</text>
    <text x="260" y="536" textAnchor="middle" fill="#4cbfe0" fontSize="10.5" fontWeight="700" letterSpacing="1.4">{s.pillars}</text>
  </svg>
)

const MaestroPage = () => {
  const { i18n } = useTranslation()
  const c = getMaestroContent(i18n.language)

  return (
    <div className="relative isolate w-full overflow-hidden bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/maestro" image="/images/maestro/cockpit.jpg" />
      <style>{`
        .mrv { transition: opacity .8s cubic-bezier(.16,.84,.28,1), transform .8s cubic-bezier(.16,.84,.28,1); }
        .mrv-off { opacity:0; transform: translateY(30px); }
        .mrv-on { opacity:1; transform:none; }
        .mflow { stroke:#2b6f86; fill:none; stroke-linecap:round; stroke-dasharray:5 9; animation: mdash 2.6s linear infinite; }
        .mfeed { stroke:rgba(201,211,58,.35); fill:none; stroke-linecap:round; stroke-dasharray:3 8; animation: mdash 3.4s linear infinite; }
        @keyframes mdash { to { stroke-dashoffset:-140; } }
        .mtok { fill:#f88116; filter: drop-shadow(0 0 2.5px rgba(248,129,22,.95)); }
        .mtok-done { fill:#c9d33a; filter: drop-shadow(0 0 3px rgba(201,211,58,.95)); }
        .mgate { animation: mgr 5.7s ease-in-out infinite; }
        @keyframes mgr { 0%,22%,100%{ stroke:rgba(25,162,207,.3);} 7%{ stroke:#f88116;} }
        .mgate-dot { animation: mgd 5.7s ease-in-out infinite; }
        @keyframes mgd { 0%,22%,100%{ fill:#2aa7cf;} 7%{ fill:#f88116;} }
        .mburn { stroke-dasharray: 170; stroke-dashoffset: 170; animation: mgrow 2s ease-out .4s forwards; }
        @keyframes mgrow { to { stroke-dashoffset: 0; } }
        @keyframes mfade { from { opacity:0; } to { opacity:1; } }
        /* the fan opens from the executed tip, like the product's chart-fan-open */
        .mfan { animation: mfanopen .8s cubic-bezier(.16,.84,.28,1) 2.3s backwards; }
        @keyframes mfanopen { from { opacity:0; transform: scale(0.03); } }
        .mlabel { animation: mfade .8s ease-out 3s backwards; }
        .mpulse-dot { animation: mpd 3s ease-in-out 3s infinite; }
        @keyframes mpd { 0%,100%{ opacity:1;} 50%{ opacity:.35;} }
        .mq { animation: mqh 9.6s ease-in-out infinite; }
        @keyframes mqh { 0%,14%,100%{ border-color:rgba(255,255,255,.10); } 4%{ border-color:rgba(25,162,207,.65); } }
        .mlift { transition: transform .3s ease, border-color .3s ease; }
        .mlift:hover { transform: translateY(-3px); border-color: rgba(25,162,207,.45); }
        @media (prefers-reduced-motion: reduce){
          .mrv{opacity:1!important;transform:none!important;transition:none!important;}
          .mflow,.mfeed,.mtok,.mtok-done,.mgate,.mgate-dot,.mq,.mpulse-dot,.mfan,.mlabel{animation:none;}
          .mburn{animation:none; stroke-dashoffset:0;}
        }
      `}</style>

      {/* ── HERO: the thesis ─────────────────────────────────────────────── */}
      <section className="relative px-5 pb-14 pt-32 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Kicker>{c.hero.kicker}</Kicker>
            <h1 className="mt-6 max-w-4xl text-[2.7rem] font-light leading-[1.02] tracking-[-0.03em] sm:text-[4.2rem]">
              {c.hero.headline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => { trackEvent('maestro_hero_cta'); scrollToId('questions') }} className="btn-board btn-board-cyan">
                {c.hero.primary}
                <span aria-hidden="true">&#8594;</span>
              </button>
            </div>
          </Reveal>
          <Reveal delay={140} className="-mx-5 mt-14 overflow-x-auto px-5 pb-3 md:mx-0 md:overflow-visible md:px-0">
            <HeroDiagram s={c.heroSvg} />
          </Reveal>
        </div>
      </section>

      {/* ── THE PATTERN: the timeless dysfunction, before AI enters ──────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl">
            <Kicker tone="text-pyramid-1">{c.pattern.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.pattern.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">{c.pattern.body}</p>
          </Reveal>
          <Reveal delay={90}>
            <div className="mt-7 flex max-w-4xl flex-wrap gap-2.5">
              {c.pattern.symptoms.map((sym) => (
                <span key={sym} className="inline-flex items-center gap-2 rounded-full border border-rose-400/20 bg-rose-400/[0.04] px-4 py-2 text-sm text-rose-100/75">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-400/60" aria-hidden="true" />
                  {sym}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-8 max-w-3xl border-t border-white/[0.08] pt-6 text-lg leading-8 text-slate-300">{c.pattern.close}</p>
          </Reveal>
        </div>
      </section>

      {/* ── WHAT CHANGED ─────────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl">
            <Kicker tone="text-pyramid-10">{c.problem.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.problem.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">{c.problem.body}</p>
          </Reveal>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-3">
            {c.problem.shifts.map((s, i) => (
              <Reveal key={s.name} delay={i * 90}>
                <span className="block h-px w-12 bg-gradient-to-r from-pyramid-10 to-transparent" />
                <p className="mt-5 text-xl font-bold text-white">{s.name}</p>
                <p className="mt-3 leading-relaxed text-slate-400">{s.detail}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE SIX QUESTIONS ────────────────────────────────────────────── */}
      <section id="questions" className="relative scroll-mt-24 px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl">
            <Kicker>{c.questions.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.questions.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">{c.questions.body}</p>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {c.questions.items.map((item, i) => (
              <Reveal key={item.q} delay={(i % 2) * 70}>
                <div className="mq mlift h-full rounded-2xl border border-white/[0.10] bg-white/[0.02] p-7" style={{ animationDelay: `${i * 1.6}s` }}>
                  <p className="text-xl font-semibold leading-snug text-white">{item.q}</p>
                  <p className="mt-3 leading-7 text-slate-400">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PILLARS ──────────────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl">
            <Kicker tone="text-pyramid-1">{c.pillars.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.pillars.title}</h2>
          </Reveal>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-3">
            {c.pillars.items.map((p, i) => {
              const tone = ['text-pyramid-19', 'text-pyramid-10', 'text-pyramid-1'][i]
              const bar = ['from-pyramid-19', 'from-pyramid-10', 'from-pyramid-1'][i]
              return (
                <Reveal key={p.name} delay={i * 90}>
                  <span className={`block h-px w-12 bg-gradient-to-r ${bar} to-transparent`} />
                  <p className={`mt-5 text-xl font-bold ${tone}`}>{p.name}</p>
                  <p className="mt-3 leading-relaxed text-slate-400">{p.detail}</p>
                </Reveal>
              )
            })}
          </div>
          <Reveal delay={120}>
            <p className="mt-12 max-w-3xl border-t border-white/[0.08] pt-6 text-lg leading-8 text-slate-300">{c.pillars.beneath}</p>
          </Reveal>
        </div>
      </section>

      {/* ── THE OPERATING MODEL ──────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal>
            <Kicker tone="text-pyramid-10">{c.model.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.model.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">{c.model.body}</p>
            <p className="mt-5 leading-7 text-slate-300">{c.model.layersOutro}</p>
            <a
              href={c.model.ctaHref}
              onClick={() => trackEvent('maestro_canon_click')}
              className="btn-board btn-board-orange mt-7"
            >
              {c.model.cta}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </Reveal>
          <Reveal delay={120} className="flex justify-center overflow-x-auto md:overflow-visible">
            <ModelDiagram s={c.model.svg} />
          </Reveal>
        </div>
      </section>

      {/* ── THE SCREENS, AS ANSWERS ──────────────────────────────────────── */}
      <section id="screens" className="relative scroll-mt-24 px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl">
            <Kicker>{c.screens.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.screens.title}</h2>
          </Reveal>
          <div className="mt-12 space-y-16">
            {c.screens.shots.map((shot, i) => (
              <Reveal key={shot.src} delay={60}>
                <figure className="grid items-center gap-6 lg:grid-cols-12">
                  <div className={`min-w-0 lg:col-span-8 ${i % 2 ? 'lg:order-2' : ''}`}>
                    <div className="max-w-full overflow-x-auto">
                      <div className="overflow-hidden rounded-xl bg-[#1a212a] ring-1 ring-white/[0.12] shadow-2xl shadow-black/50">
                        <img src={shot.src} alt={shot.alt} loading="lazy" className="block w-full" />
                      </div>
                    </div>
                  </div>
                  <figcaption className={`lg:col-span-4 ${i % 2 ? 'lg:order-1 lg:text-right' : ''}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-pyramid-19">{shot.q}</p>
                    {/* two readings: the executive meaning, then the operational evidence */}
                    {shot.lead ? <p className="mt-3 text-lg font-semibold leading-snug text-white">{shot.lead}</p> : null}
                    <p className="mt-3 text-sm leading-7 text-slate-400">{shot.caption}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── BEYOND TRACKING ──────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <Kicker tone="text-pyramid-1">{c.contrast.kicker}</Kicker>
            <h2 className="mt-4 text-[2rem] font-light leading-[1.08] tracking-[-0.02em] sm:text-[2.6rem]">{c.contrast.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">{c.contrast.body}</p>
          </Reveal>
          <Reveal delay={120} className="space-y-4">
            {c.contrast.cards.map((card, i) => (
              <div key={card.title} className={`rounded-2xl border p-8 ${i ? 'border-pyramid-10/25 bg-pyramid-10/[0.04]' : 'border-pyramid-19/25 bg-pyramid-19/[0.04]'}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${i ? 'text-pyramid-10' : 'text-pyramid-19'}`}>{card.title}</p>
                <p className="mt-4 leading-8 text-slate-300">{card.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── STATUS ───────────────────────────────────────────────────────── */}
      <section className="relative px-5 pb-4 pt-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 md:p-10">
              <Kicker>{c.status.kicker}</Kicker>
              <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-300">{c.status.body}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaClose lane="maestro" page="maestro" />
    </div>
  )
}

export default MaestroPage
